import ReactQuill from "react-quill-new";
import { useFormContext } from "react-hook-form";
import { useEffect, useMemo, useRef } from "react";
import "quill/dist/quill.snow.css";
import { getNestedError } from "../../../utils";
import Quill from "quill";

const CustomUndo = () => (
  <svg viewBox="0 0 18 18">
    <polygon className="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10" />
    <path
      className="ql-stroke"
      d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9"
    />
  </svg>
);

const CustomRedo = () => (
  <svg viewBox="0 0 18 18">
    <polygon className="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10" />
    <path
      className="ql-stroke"
      d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5"
    />
  </svg>
);

const Image = Quill.import("formats/image");
class CustomImageBlot extends Image {
  static create(value) {
    const node = super.create(value.src || value);

    if (typeof value === "object") {
      node.setAttribute("src", value.src);
      if (value.id) node.setAttribute("id", value.id);
    }

    return node;
  }

  static value(node) {
    return {
      src: node.getAttribute("src"),
      id: node.getAttribute("id"),
    };
  }
}

CustomImageBlot.blotName = "image";
CustomImageBlot.tagName = "img";

Quill.register(CustomImageBlot, true);

const Font = Quill.import("formats/size");
Font.whitelist = [
  "8px",
  "9px",
  "10px",
  "11px",
  "12px",
  "14px",
  "16px",
  "18px",
  "20px",
  "22px",
  "24px",
  "26px",
  "28px",
  "36px",
  "48px",
  "72px",
];
Quill.register(Font, true);

const MyEditor = (props) => {
  const { name, onChange = () => {}, ...rest } = props;

  const {
    setValue,
    watch,
    trigger,
    clearErrors,
    getValues,
    formState: { errors },
  } = useFormContext();

  const value = watch(name);

  const quillRef = useRef(null);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: "#toolbar",
        handlers: {
          undo: () => quillRef.current.getEditor().history.undo(),
          redo: () => quillRef.current.getEditor().history.redo(),
          image: imageHandler,
        },
        history: {
          delay: 500,
          maxStack: 100,
          userOnly: true,
        },
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function imageHandler() {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        if (file) {
          const reader = new FileReader();
          const quill = quillRef.current.getEditor();
          const range = quill.getSelection();

          const fileId = `temp-fileId-${new Date().getTime()}`;
          const embeddedFiles = getValues("embeddedFiles");
          const newEmbeddedFiles = [
            ...(embeddedFiles || []),
            {
              file,
              name: file.name,
              fileId,
            },
          ];

          setValue("embeddedFiles", newEmbeddedFiles);

          reader.onloadend = () => {
            if (range.index != null) {
              quill.insertEmbed(range.index, "image", reader.result);
              setTimeout(() => {
                const img = quill.root.querySelector(
                  `img[src="${reader.result}"]`
                );
                if (img) {
                  img.setAttribute("id", fileId);
                  img.setAttribute("class", "inline");
                }
              }, 0);
            }
          };
          reader.readAsDataURL(file);
        }
      }
    };
  }

  // handle change value
  const handleChange = (content) => {
    setValue(name, content, { shouldValidate: true });
    onChange(content);
  };

  const formats = [
    "header",
    "size",
    "bold",
    "italic",
    "underline",
    "align",
    "strike",
    "script",
    "blockquote",
    "background",
    "list",
    "indent",
    "link",
    "image",
    "color",
    "code-block",
  ];

  useEffect(() => {
    if (value) trigger(name);
    else clearErrors(name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className="editor-wrapper">
      <div id="toolbar">
        <span className="ql-formats">
          <button className="ql-undo">
            <CustomUndo />
          </button>
          <button className="ql-redo">
            <CustomRedo />
          </button>
        </span>
        <span className="line" />
        <span className="ql-formats">
          <button className="ql-link" />
          <button className="ql-image" />
        </span>
        <span className="line" />
        <span className="ql-formats">
          <button className="ql-bold" />
          <button className="ql-italic" />
          <button className="ql-underline" />
          <button className="ql-strike" />
        </span>
        <span className="line" />
        <span className="ql-formats">
          <select className="ql-header" defaultValue="3">
            <option value="1">Heading</option>
            <option value="2">Heading 2</option>
            <option value="3">Normal</option>
          </select>
          <select className="ql-color" />
          <select className="ql-background" />
        </span>
        <span className="line" />
        <span className="ql-formats">
          <select className="ql-size" defaultValue="16px">
            <option value="8px">8px</option>
            <option value="9px">9px</option>
            <option value="10px">10px</option>
            <option value="11px">11px</option>
            <option value="12px">12px</option>
            <option value="14px">14px</option>
            <option value="16px">16px</option>
            <option value="18px">18px</option>
            <option value="20px">20px</option>
            <option value="22px">22px</option>
            <option value="24px">24px</option>
            <option value="26px">26px</option>
            <option value="28px">28px</option>
            <option value="36px">36px</option>
            <option value="48px">48px</option>
            <option value="72px">72px</option>
          </select>
        </span>
        <span className="line" />

        <span className="ql-formats">
          <button className="ql-align" value="" />
          <button className="ql-align" value="center" />
          <button className="ql-align" value="right" />
          <button className="ql-align" value="justify" />
        </span>
        <span className="line" />
        <span className="ql-formats">
          <button className="ql-list" value="ordered" />
          <button className="ql-list" value="bullet" />
          <button className="ql-indent" value="-1" />
          <button className="ql-indent" value="+1" />
        </span>
        <span className="line" />
        <span className="ql-formats">
          <button className="ql-blockquote" />
          <button className="ql-code-block" />
        </span>
        <span className="line" />
        <span className="ql-formats">
          <button className="ql-clean" />
        </span>
      </div>
      <ReactQuill
        id={name}
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={handleChange}
        modules={modules}
        className="my-editor"
        formats={formats}
        {...rest}
      />
      {getNestedError(errors, name) && (
        <p
          className="text-error text-sm"
          style={{
            letterSpacing: "-0.03em",
          }}
        >
          {getNestedError(errors, name)?.message}
        </p>
      )}
    </div>
  );
};

export default MyEditor;
